import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzInputModule } from 'ng-zorro-antd/input';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzMessageService } from 'ng-zorro-antd/message';

import {
  CancelPackagePurchaseDto,
  ServicePackagePurchase,
  ServicePackageService,
  SettlementDay,
} from '@beautyapp/core';

/**
 * Cancellation of an UNUSED package sold on an earlier day — beaut-484.
 *
 * An unused package used to be cancelled through a bare confirm that posted
 * `{}`, so its reversal always hit today's till. The partly-used path already
 * asks which day the money settles on (the refund modal); this asks the same
 * question here, against the same `/cancel` endpoint, which has accepted
 * `settlementDay` all along.
 *
 * Only opened when the sale was on an earlier day. Sold today, both answers are
 * the same till, and callers keep the plain confirm.
 */
@Component({
  selector: 'beautyapp-package-cancel-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzFormModule,
    NzRadioModule,
    NzButtonModule,
    NzInputModule,
    TranslateModule,
  ],
  templateUrl: './package-cancel-modal.component.html',
})
export class PackageCancelModalComponent {
  private modalRef = inject(NzModalRef);
  private packageService = inject(ServicePackageService);
  private message = inject(NzMessageService);
  private translate = inject(TranslateService);
  private data: { purchase: ServicePackagePurchase } = inject(NZ_MODAL_DATA);

  readonly purchase = this.data.purchase;
  readonly today = new Date();
  readonly saleDate = this.purchase.purchasedAt ?? this.purchase.createdAt;

  submitting = signal(false);

  form = new FormGroup({
    // TODAY is what the backend does when the field is absent, so the default
    // posts exactly the payload the old confirm did.
    settlementDay: new FormControl<SettlementDay>('TODAY', { nonNullable: true }),
    reason: new FormControl<string>('', { nonNullable: true }),
  });

  submit(): void {
    if (this.submitting()) return;
    this.submitting.set(true);
    this.setDismissable(false);

    const v = this.form.getRawValue();
    const reason = v.reason.trim();
    const dto: CancelPackagePurchaseDto = {
      ...(reason ? { reason } : {}),
      ...(v.settlementDay === 'SALE_DATE' ? { settlementDay: 'SALE_DATE' as const } : {}),
    };

    this.packageService
      .cancelPurchase(this.purchase.id, dto)
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe({
        next: () => {
          this.message.success(this.translate.instant('servicePackages.messages.cancelled'));
          this.modalRef.close(true);
        },
        error: (err: { error?: { message?: string } }) => {
          this.setDismissable(true);
          this.message.error(
            err?.error?.message || this.translate.instant('servicePackages.messages.cancelError')
          );
        },
      });
  }

  cancel(): void {
    // Closing with false mid-request would skip the caller's reload even if
    // the cancellation then succeeds, leaving a stale active row.
    if (this.submitting()) return;
    this.modalRef.close(false);
  }

  /** The X, the mask and Esc all close the modal too, not just our button. */
  private setDismissable(dismissable: boolean): void {
    this.modalRef.updateConfig({
      nzClosable: dismissable,
      nzMaskClosable: dismissable,
      nzKeyboard: dismissable,
    });
  }
}
